/**
 * The DOM half of the control mapping: window key listeners feeding a set of
 * held keys, which `keys.ts` turns into sim inputs. Everything decision-shaped
 * lives in `keys.ts`; this file is listeners and a `Set`.
 */

import { discreteCommandFor, isFlightKey, normalizeKey, type DiscreteCommand } from "./keys.ts";

export interface KeyboardControls {
  /** The keys held right now, in `keys.ts`'s normalized form. */
  pressed(): ReadonlySet<string>;
  dispose(): void;
}

/**
 * Whether the key went to something the player is *typing into*.
 *
 * Both name prompts — the Mission Recorder's tape name and BEST MISSIONS'
 * *"WHAT IS YOUR PILOT'S NAME?"* — are mounted inside `FlightSurface`, so their
 * keystrokes reach this listener before anything else. Without this guard every
 * flight key is `preventDefault()`ed on the way to the text box, and the player
 * cannot type his own name: the letters pitch the aeroplane, open the map and
 * toggle the pause instead (user report, 2026-08-25; found independently by the
 * completeness audit the same day).
 *
 * `SELECT` is on the list because its own key handling — type-ahead and the
 * arrows that move the selection — is the same thing in a different shape.
 */
function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT";
}

export function createKeyboardControls(onCommand: (command: DiscreteCommand) => void): KeyboardControls {
  const pressed = new Set<string>();

  function onKeyDown(event: KeyboardEvent): void {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (isTextEntryTarget(event.target)) return;
    const key = normalizeKey(event.key);
    if (!isFlightKey(key)) return;
    // Arrows scroll the page and the throttle keys can zoom it; the flight
    // keys belong to the aircraft while this route is mounted.
    event.preventDefault();
    // `repeat` events must not re-trigger a one-shot command (holding P would
    // otherwise toggle pause dozens of times a second).
    if (!event.repeat) {
      const command = discreteCommandFor(key);
      if (command) onCommand(command);
    }
    pressed.add(key);
  }

  function onKeyUp(event: KeyboardEvent): void {
    pressed.delete(normalizeKey(event.key));
  }

  /** A window that loses focus never delivers the matching keyup — drop everything. */
  function onBlur(): void {
    pressed.clear();
  }

  window.addEventListener("keydown", onKeyDown, { passive: false });
  window.addEventListener("keyup", onKeyUp);
  window.addEventListener("blur", onBlur);

  return {
    pressed: () => pressed,
    dispose: () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
      pressed.clear();
    },
  };
}
